<template>
  <div
    class="ma-0 pa-0 workflow-action-node-root"
    :data-node-id="this.node.id"
  >
    <v-card
      v-if="((this.node.data.type != 'delay') && this.node.data.delay)"
      class="px-3 py-1 text-center background workflow-action-node-card"
      elevation="0"
      height="32"
      style="position: absolute; top: -48px; left: 87px;"
    >
      <v-card-text
        class="py-0"
      >
        <div>
          Wait
          {{ this.node.data.delay }}
          {{ (this.node.data.delay == 1) ? 'hour' : 'hours' }}
        </div>
      </v-card-text>
    </v-card>
    <v-badge
      :content="this.node.data.total_contacts"
      :model-value="this.node.data.total_contacts > 0"
      color="orange"
      class="workflow-action-contacts-badge"
      overlap
    >
      <v-card
        class="workflow-canvas-drag-handle flowy-drag-handle workflow-action-node-card"
        :color="node.color"
        :elevation="1"
        :class="{ 'removed': this.node.removed, 'detached': this.node.detached }"
        @dblclick="editNode"
        width="300"
        min-height="50"
      >
        <v-toolbar
          flat
          density="compact"
          color="light-blue-darken-4"
          class="text-white workflow-action-node-toolbar"
        >

          <v-menu
            v-if="this.node.data.type != 'start'"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                icon
                size="small"
                class="mr-1 nodrag"
                v-bind="props"
              ><v-icon size="small">mdi-menu</v-icon></v-btn>
            </template>

            <v-list>
              <v-list-item
                v-if="!this.node.removed"
                @click="editNode()"
              >
                <template #title>Edit</template>
              </v-list-item>
              <v-list-item
                v-if="!this.node.removed"
                @click="toggleRemoved()"
              >
                <template #title>Remove</template>
              </v-list-item>
              <v-list-item
                v-if="this.node.removed"
                @click="toggleRemoved()"
              >
                <template #title>Restore</template>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-icon
            v-if="this.node.data.type == 'start'"
            size="small"
            class="ml-3"
          >mdi-rocket-launch</v-icon>
          <v-icon
            v-else-if="getNodeIcon(this.node.data.type)"
            size="small"
          >{{ getNodeIcon(this.node.data.type) }}</v-icon>

          <v-toolbar-title
            class="text-subtitle-1 pl-3 workflow-action-node-title"
          >
            {{ title }}
          </v-toolbar-title>

          <base-warning-icon
            v-if="isListMissing(this.node)"
            label="Please, select a list."
          />
          <base-warning-icon
            v-if="isTagMissing(this.node)"
            label="Please, select a tag."
          />
          <base-warning-icon
            v-if="isNoteMissing(this.node)"
            label="Please, select a note."
          />
          <base-warning-icon
            v-if="isTaskMissing(this.node)"
            label="Please, select a task."
          />
          <base-warning-icon
            v-if="isWorkflowMissing(this.node)"
            label="Please, select a workflow."
          />
          <base-warning-icon
            v-if="isLinkedinMissing(this.node)"
            label="Please, select a LinkedIn account."
          />

          <!--
            Avoid v-spacer (flex-grow:1) beside V3's flex title — they split
            free space and truncate the title too early. ml-auto keeps icons right.
          -->
          <div class="d-flex align-center ml-auto flex-nowrap">
          <v-tooltip
            v-if="this.node.new"
            location="bottom"
            max-width="300"
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                size="small"
              >mdi-plus</v-icon>
            </template>
            <span>This action is new and will be created when the workflow is saved</span>
          </v-tooltip>

          <v-tooltip
            v-if="this.node.removed"
            location="bottom"
            max-width="300"
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                size="small"
              >mdi-minus</v-icon>
            </template>
            <span>This action has been removed and will be deleted when the workflow is saved</span>
          </v-tooltip>

          <v-tooltip
            v-if="this.node.detached"
            location="bottom"
            max-width="300"
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                size="small"
              >mdi-connection</v-icon>
            </template>
            <span>This action is detached or attached to a removed action and will be deleted when the workflow is saved</span>
          </v-tooltip>

          <v-tooltip
            v-if="this.node.changed"
            location="bottom"
            max-width="300"
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                size="small"
              >mdi-pencil</v-icon>
            </template>
            <span>This action has been edited and will be updated when the workflow is saved</span>
          </v-tooltip>

          <v-tooltip
            v-if="this.node.moved"
            location="bottom"
            max-width="300"
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                size="small"
              >mdi-cursor-move</v-icon>
            </template>
            <span>This action has been moved and will be updated when the workflow is saved</span>
          </v-tooltip>

          <v-tooltip location="bottom"
            max-width="300"
          >
            <template v-slot:activator="{ props }">
              <v-icon
                class="mr-3"
                v-bind="props"
              >mdi-drag</v-icon>
            </template>
            <span>Drag this action to change its position</span>
          </v-tooltip>
          </div>
        </v-toolbar>

        <v-card-text>
          <div v-html="description"></div>
          <div
            v-if="this.node.data.timeout"
            class="mt-3"
          >
            Timeout: <b>{{ this.node.data.timeout }}</b> hours.
          </div>
          <div
            v-if="this.node.data.details && this.node.data.details.message"
            class="mt-3"
          >
            <v-alert
              dark
              density="compact"
              variant="outlined"
              color="blue-grey"
              style="white-space: pre-line; overflow: hidden;"
            >
              {{ (this.node.data.details.message.length > 100) ? this.node.data.details.message.substring(0,97) + "..." : this.node.data.details.message }}
            </v-alert>
          </div>
          <div
            v-if="this.node.data.linkedin"
            class="mt-3"
          >
            As
            <base-linkedin-account
              v-model="this.node.data.linkedin"
              @click="editNode()"
            ></base-linkedin-account>
          </div>
        </v-card-text>
      </v-card>
    </v-badge>
    <v-btn
      v-if="(this.node.hasChildren && this.node.hasSlots)"
      elevation="1"
      icon
      size="x-small"
      rounded="circle"
      class="mt-7 nodrag vf-add-child-btn"
      @click="newNode"
    >
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-icon
            v-bind="props"
          >mdi-plus</v-icon>
        </template>
        <span>Add Action</span>
      </v-tooltip>
    </v-btn>
	</div>
</template>

<script>
import Workflow from "@/helpers/Workflow";

export default {
  name: 'WorkflowActionNode',

  props: {
    remove: {
      type: Function,
      required: true,
    },
    node: {
      type: Object,
      required: true,
    },
    icon: {
      type: String,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: false,
    },
  },

  data () {
    return {

    };
  },

  mounted () {

  },

  unmounted() {

  },

  methods: {

    newNode () {
      if (typeof this.node.requestAddChild === 'function') {
        this.node.requestAddChild();
        return;
      }
      // Legacy Flowy fallback
      this.node.addNew = true;
      this.remove();
    },

    editNode () {
      if (this.node.data.type == 'start') return;
      if (typeof this.node.requestEdit === 'function') {
        this.node.requestEdit();
        return;
      }
      // Legacy Flowy fallback
      this.node.edit = true;
      this.remove();
    },

    toggleRemoved () {
      if (this.node.new) {
        // Discard unsaved new node via Flowy remove path
        this.remove();
        return;
      }
      if (typeof this.node.toggleRemoved === 'function') {
        this.node.toggleRemoved();
        return;
      }
      this.remove();
    },

    isParameterMissing (node, type, value) {
      if ('parameter' in node.data) return false;
      if (node.data.type.startsWith(type + '_') &&
          !value) {
        return true;
      }
      return false;
    },

    isListMissing (node) {
      return this.isParameterMissing(node, 'list', node.data.contact_list);
    },

    isTagMissing (node) {
      return this.isParameterMissing(node, 'tag', node.data.tag);
    },

    isNoteMissing (node) {
      return this.isParameterMissing(node, 'note', node.data.note);
    },

    isTaskMissing (node) {
      return this.isParameterMissing(node, 'task', node.data.task);
    },

    isWorkflowMissing (node) {
      return this.isParameterMissing(node, 'workflow', node.data.workflow);
    },

    isLinkedinMissing (node) {
      return this.isParameterMissing(node, 'linkedin', node.data.linkedin);
    },

    getNodeIcon: function (type) {
      return Workflow.getTypeIcon(type);
    },

  },
};
</script>

<style scoped>
.workflow-action-node-root {
  position: relative;
}
/* Master V2 card default was 4px; global VCard default is sm (2px). */
.workflow-action-node-card {
  border-radius: 4px !important;
}
/*
 * Master V2 orange badge: Material orange (#FF9800) + white label.
 * V3 theme contrast often paints dark text on orange — force the V2 pair.
 */
.workflow-action-contacts-badge :deep(.v-badge__badge) {
  background-color: #ff9800 !important;
  color: #ffffff !important;
}
/* V3 compact toolbar is tighter than V2 app-bar; restore master side insets. */
.workflow-action-node-toolbar :deep(.v-toolbar__content) {
  padding-inline: 8px;
}
/*
 * V3 toolbar-title defaults to flex:1 1 and ellipsizes early when a spacer
 * also grows. Give the title remaining width (icons use ml-auto instead).
 */
.workflow-action-node-toolbar :deep(.workflow-action-node-title),
.workflow-action-node-toolbar :deep(.v-toolbar-title) {
  flex: 1 1 auto !important;
  min-width: 0;
  max-width: none !important;
  overflow: hidden;
}
.workflow-action-node-toolbar :deep(.v-toolbar-title__placeholder) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.v-card.removed > :not(.v-card__progress) {
  opacity: 0.6;
}
.v-card.detached > :not(.v-card__progress) {
  opacity: 0.6;
}
/* Master Flowy: + sits under the card, centered toward the bottom edge */
.vf-add-child-btn {
  position: absolute !important;
  left: 50%;
  bottom: -16px;
  transform: translateX(-50%);
  z-index: 2;
}
</style>
